import type { Analisis, Colocacion, Pieza, Punto, ResultadoNesting } from '@laser/core';

export interface Caja { minX: number; minY: number; maxX: number; maxY: number }

export function cajaDe(puntos: Punto[]): Caja {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of puntos) {
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  }
  if (!puntos.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Rota alrededor del origen en coordenadas de pantalla (y hacia abajo): 90° = sentido horario. */
export function rotarPunto([x, y]: Punto, grados: number): Punto {
  const g = ((Math.round(grados) % 360) + 360) % 360;
  switch (g) {
    case 0: return [x, y];
    case 90: return [-y, x];
    case 180: return [-x, -y];
    case 270: return [y, -x];
    default: {
      const r = (g * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
      return [x * c - y * s, x * s + y * c];
    }
  }
}

/**
 * Coloca una pieza en la hoja: rota su contorno y lo traslada para que la esquina
 * superior izquierda de la caja envolvente ya rotada quede en (x, y) de la colocación.
 */
export function transformarPieza(pieza: Pieza, col: Pick<Colocacion, 'x' | 'y' | 'rotacionGrados'>): { exterior: Punto[]; agujeros: Punto[][]; caja: Caja } {
  const rot = (pts: Punto[]) => pts.map((p) => rotarPunto(p, col.rotacionGrados));
  const ext = rot(pieza.exterior);
  const agu = pieza.agujeros.map(rot);
  const c = cajaDe(ext);
  const dx = col.x - c.minX, dy = col.y - c.minY;
  const mover = (pts: Punto[]) => pts.map(([x, y]) => [x + dx, y + dy] as Punto);
  const exterior = mover(ext);
  return {
    exterior,
    agujeros: agu.map(mover),
    caja: { minX: col.x, minY: col.y, maxX: col.x + (c.maxX - c.minX), maxY: col.y + (c.maxY - c.minY) },
  };
}

/** Tamaño de la caja de la pieza tras rotar (90/270 intercambian ancho y alto). */
export function tamanoRotado(anchoMm: number, altoMm: number, grados: number): [number, number] {
  const g = ((Math.round(grados) % 360) + 360) % 360;
  return g === 90 || g === 270 ? [altoMm, anchoMm] : [anchoMm, altoMm];
}

export interface Disposicion {
  hojas: { indice: number; x: number; y: number }[];
  anchoTotal: number; altoTotal: number;
}

/** Acomoda N hojas en una rejilla con `columnas` y separación, en mm. */
export function disponerHojas(n: number, hojaAncho: number, hojaAlto: number, columnas = 2, separacion = 40): Disposicion {
  const cols = Math.max(1, Math.min(columnas, n));
  const filas = Math.max(1, Math.ceil(n / cols));
  const hojas = Array.from({ length: n }, (_, i) => ({
    indice: i,
    x: (i % cols) * (hojaAncho + separacion),
    y: Math.floor(i / cols) * (hojaAlto + separacion),
  }));
  return {
    hojas,
    anchoTotal: cols * hojaAncho + (cols - 1) * separacion,
    altoTotal: filas * hojaAlto + (filas - 1) * separacion,
  };
}

/** "M x y L x y … Z" con 2 decimales. */
export function rutaPoligono(puntos: Punto[], cerrar = true): string {
  if (!puntos.length) return '';
  const f = (n: number) => (Math.round(n * 100) / 100).toString();
  return puntos.map(([x, y], i) => `${i ? 'L' : 'M'}${f(x)} ${f(y)}`).join(' ') + (cerrar ? ' Z' : '');
}

export interface PiezaDibujada {
  clave: string; hoja: number; d: string; caja: Caja; archivoId: string; piezaId: string; copia: number;
}

/** Convierte un resultado de nesting en rutas SVG listas para pintar (coordenadas globales del lienzo). */
export function dibujarNesting(n: ResultadoNesting, analisis: Record<string, Analisis | null | undefined>, disp: Disposicion): PiezaDibujada[] {
  const out: PiezaDibujada[] = [];
  for (const col of n.colocaciones) {
    const hoja = disp.hojas[col.hoja];
    if (!hoja) continue;
    const pieza = analisis[col.archivoId]?.piezas.find((p) => p.id === col.piezaId);
    const desplazar = (c: Caja): Caja => ({ minX: c.minX + hoja.x, minY: c.minY + hoja.y, maxX: c.maxX + hoja.x, maxY: c.maxY + hoja.y });
    const clave = `${col.archivoId}:${col.piezaId}:${col.copia}`;
    if (!pieza) continue;
    const t = transformarPieza(pieza, col);
    const mover = (pts: Punto[]) => pts.map(([x, y]) => [x + hoja.x, y + hoja.y] as Punto);
    const d = [rutaPoligono(mover(t.exterior)), ...t.agujeros.map((a) => rutaPoligono(mover(a)))].join(' ');
    out.push({ clave, hoja: col.hoja, d, caja: desplazar(t.caja), archivoId: col.archivoId, piezaId: col.piezaId, copia: col.copia });
  }
  return out;
}
